import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { BASE_URL } from '../../../config';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Box,
  Chip,
  Divider,
  IconButton,
  CardMedia,
  Grid,
  Tooltip,
  Avatar
} from '@mui/material';
import { styled } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import PaletteIcon from '@mui/icons-material/Palette';
import { getLocalizedField } from '../../../utils/localizedFields';
import GroupsIcon from '@mui/icons-material/Groups';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PersonIcon from '@mui/icons-material/Person';
import { theme } from '../../../theme';

// Styled components for better visuals
const SalonCard = styled(Card)(({ theme }) => ({
  height: '100%',
  width: '100%',
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  borderRadius: theme.borderRadius?.xl || '16px',
  border: `2px solid ${theme.colors?.primary?.[100] || '#e3f2fd'}`,
  background: `linear-gradient(135deg, ${theme.colors?.background?.primary || '#ffffff'} 0%, ${theme.colors?.secondary?.[50] || '#f8f9fa'} 100%)`,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  boxShadow: `0 4px 20px ${theme.colors?.primary?.[50] || 'rgba(0,0,0,0.08)'}`,
  '&:hover': {
    boxShadow: `0 12px 40px ${theme.colors?.primary?.[100] || 'rgba(0,0,0,0.15)'}`,
    border: `2px solid ${theme.colors?.primary?.[200] || '#bbdefb'}`
  }
}));

// Helper: get today's name in French matching the keys used in opening_hours
const getTodayFrench = () => {
  const days = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];
  return days[new Date().getDay()];
};

// Helper: stable order for French days
const FRENCH_DAYS_ORDER = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'];

// Helper: map French day names to translation keys
const getDayTranslationKey = (frenchDay) => {
  const dayMap = {
    'lundi': 'days.monday',
    'mardi': 'days.tuesday',
    'mercredi': 'days.wednesday',
    'jeudi': 'days.thursday',
    'vendredi': 'days.friday',
    'samedi': 'days.saturday',
    'dimanche': 'days.sunday'
  };
  return dayMap[frenchDay?.toLowerCase()] || frenchDay;
};

const BasicInfo = ({
  salon,
  onDelete,
  onUpdate,
  onManageUnavailability,
  onManageTheme,
  showActions = true,
  minHeight,
  showOpeningHours = true,
  employeesCount,
  ownerLabel,
  metaIconsOnly = false,
}) => {
  const { t, i18n } = useTranslation();

  const getLocalizedName = (salon) => {
    return getLocalizedField(salon, 'name', i18n.language);
  };

  // State for photo carousel
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [salonPhotos, setSalonPhotos] = useState([]);
  
  // Format opening hours for display
  const formatOpeningHours = (openingHours) => {
    if (!openingHours) return 'Non spécifié';
    
    // Find the first day that's open
    const firstOpenDay = Object.entries(openingHours).find(
      ([day, hours]) => hours.ouverture !== 'fermé'
    );
    
    if (!firstOpenDay) return t('pro_dashboard.common.closed');
    
    const [day, hours] = firstOpenDay;
    return `${day.charAt(0).toUpperCase() + day.slice(1)}: ${hours.ouverture} - ${hours.fermeture}`;
  };
  
  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  // State to store the formatted address from coordinates
  const [locationAddress, setLocationAddress] = useState('');

  // Function to convert coordinates to address using Nominatim
  const getAddressFromCoordinates = async (latitude, longitude) => {
    if (!latitude || !longitude) return null;
    
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`,
        { headers: { 'Accept-Language': 'fr' } }
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch address');
      }
      
      const data = await response.json();
      return data.display_name || null;
    } catch (error) {
      console.error('Error fetching address:', error);
      return null;
    }
  };

  // Effect to fetch address when salon has coordinates
  useEffect(() => {
    const fetchAddress = async () => {
      if (salon.latitude && salon.longitude) {
        const address = await getAddressFromCoordinates(salon.latitude, salon.longitude);
        if (address) {
          setLocationAddress(address);
        }
      }
    };
    
    fetchAddress();
  }, [salon]);
  
  // Get all salon image URLs - UPDATED VERSION
  const getSalonImages = () => {
    const images = [];
    
    if (!salon.photos || !Array.isArray(salon.photos) || salon.photos.length === 0) {
      return []; // Return empty array instead of default image
    }
    
    // Sort photos to put primary photo first
    const sortedPhotos = [...salon.photos].sort((a, b) => {
      if (typeof a === 'object' && a !== null && a.is_primary) return -1;
      if (typeof b === 'object' && b !== null && b.is_primary) return 1;
      return 0;
    });
    
    // Simplified URL construction function
    const buildImageUrl = (url) => {
      if (!url) return null;
            
      // If URL is already absolute, return as is
      if (/^https?:\/\//i.test(url)) {
        // Check if it's a localhost URL without port and add port 8012
        if (url.match(/^http:\/\/localhost\//) && !url.match(/^http:\/\/localhost:[0-9]+\//)) {
          return url.replace('http://localhost/', 'http://localhost:8012/');
        }
        return url;
      }
      
      // For relative URLs that start with /media/
      if (url.startsWith('/media/')) {
        // Use port 8012 for localhost in development
        const baseUrl = window.location.hostname === 'localhost' ? 
          'http://localhost:8012' : 
          BASE_URL.replace(/\/$/, ''); // Remove trailing slash
        const finalUrl = `${baseUrl}${url}`;
        return finalUrl;
      }
      
      // For other relative URLs, prepend BASE_URL
      const baseUrl = window.location.hostname === 'localhost' ? 
        'http://localhost:8012' : 
        BASE_URL.replace(/\/$/, ''); // Remove trailing slash
      const cleanUrl = url.startsWith('/') ? url : `/${url}`;
      const finalUrl = `${baseUrl}${cleanUrl}`;
      return finalUrl;
    };
    
    // Process each photo
    sortedPhotos.forEach(photo => {      
      let imageUrl = null;
      
      // Case 1: Photo is a string (direct URL)
      if (typeof photo === 'string') {
        imageUrl = buildImageUrl(photo);
      } 
      // Case 2: Photo is an object with url property
      else if (photo && typeof photo === 'object' && photo.url) {
        imageUrl = buildImageUrl(photo.url);
      }
      
      if (imageUrl) {
        images.push(imageUrl);
      }
    });
    
    return images;
  };
  
  // Navigation functions for photo carousel
  const handlePrevPhoto = (e) => {
    e.stopPropagation();
    setCurrentPhotoIndex(prev => 
      prev === 0 ? salonPhotos.length - 1 : prev - 1
    );
  };
  
  const handleNextPhoto = (e) => {
    e.stopPropagation();
    setCurrentPhotoIndex(prev => 
      prev === salonPhotos.length - 1 ? 0 : prev + 1
    );
  };
  
  // Effect to initialize salon photos
  useEffect(() => {
    const photos = getSalonImages();
    setSalonPhotos(photos);
    // Reset to first (which is sorted to primary) when salon data changes
    setCurrentPhotoIndex(0);
  }, [salon]);
  
  // Check if we have actual photos
  const hasPhotos = salonPhotos && salonPhotos.length > 0;
  const currentSalonImage = hasPhotos ? salonPhotos[currentPhotoIndex] : null;

  return (
    <SalonCard 
      elevation={2} 
      onClick={() => (typeof onUpdate === 'function' ? onUpdate(salon) : undefined)}
      sx={{ cursor: typeof onUpdate === 'function' ? 'pointer' : 'default', minHeight }}
    >
      {/* Card Header with Photo and Gradient Overlay */}
      <Box sx={{ position: 'relative', height: 200, overflow: 'hidden' }}>
        {/* Action buttons in top right corner */}
        {showActions && (
          <Box 
            onClick={(e) => e.stopPropagation()}
            sx={{ 
              position: 'absolute', 
              top: 8, 
              right: 8, 
              zIndex: 10,
              display: 'flex',
              gap: 1
            }}
          >
            {typeof onManageUnavailability === 'function' && (
              <Tooltip
                title={`Gérer les congés et absences - ${getLocalizedName(salon)}`}
                placement="top"
              >
                <IconButton
                  size="small"
                  aria-label={`Gérer les congés et absences - ${getLocalizedName(salon)}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    onManageUnavailability(salon);
                  }}
                  sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    color: theme.colors?.warning?.[700] || '#a15c21',
                    backdropFilter: 'blur(4px)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                    '&:hover': {
                      backgroundColor: theme.colors?.warning?.[50] || '#fff7ed',
                    },
                  }}
                >
                  <EventBusyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {typeof onManageTheme === 'function' && (
              <Tooltip title={t('salon_theme.actions.open')} placement="top">
                <IconButton
                  size="small"
                  aria-label={`${t('salon_theme.actions.open')} - ${getLocalizedName(salon)}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    onManageTheme(salon);
                  }}
                  sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    color: theme.colors?.secondary?.[700] || '#a67d47',
                    width: 36,
                    height: 36,
                  }}
                >
                  <PaletteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {typeof onUpdate === 'function' && <Tooltip title="Modifier" placement="top">
              <IconButton 
                size="small" 
                onClick={(e) => {
                  e.stopPropagation();
                  if (typeof onUpdate === 'function') onUpdate(salon);
                }} 
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(4px)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                  '&:hover': {
                    backgroundColor: theme.colors?.primary?.[500] || '#1976d2',
                    color: '#ffffff',
                    transform: 'scale(1.1)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                  },
                  transition: 'all 0.2s ease',
                  width: 36,
                  height: 36
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>}
            {typeof onDelete === 'function' && <Tooltip title="Supprimer" placement="top">
              <IconButton 
                size="small" 
                onClick={(e) => {
                  e.stopPropagation();
                  if (typeof onDelete === 'function') onDelete();
                }} 
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(4px)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                  '&:hover': {
                    backgroundColor: theme.colors?.error?.[500] || '#d32f2f',
                    color: '#ffffff',
                    transform: 'scale(1.1)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                  },
                  transition: 'all 0.2s ease',
                  width: 36,
                  height: 36
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>}
          </Box>
        )}
        {hasPhotos ? (
          <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
            <CardMedia
              component="img"
              height="200"
              image={currentSalonImage}
              alt={`Salon ${getLocalizedName(salon)}`}
              sx={{
                objectFit: 'cover',
                width: '100%',
                height: '100%'
              }}
              onError={(e) => {
                console.error('Image failed to load:', currentSalonImage);
              }}
            />
            {/* Gradient overlay */}
            <Box sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: `linear-gradient(135deg, ${theme.colors?.primary?.[600] || '#1565c0'}99 0%, ${theme.colors?.secondary?.[600] || '#6c757d'}99 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2,
              p: 3,
              pointerEvents: 'none'
            }}>
              <Typography 
                variant="h4" 
                component="h2" 
                fontWeight="bold"
                sx={{
                  color: '#ffffff',
                  textShadow: '0 3px 12px rgba(0,0,0,0.7)',
                  fontSize: '2rem',
                  letterSpacing: '0.5px',
                  textAlign: 'center',
                  position: 'relative',
                  zIndex: 3
                }}
              >
                {getLocalizedName(salon)}
              </Typography>
            </Box>
            {/* Photo navigation - only if multiple photos */}
            {salonPhotos.length > 1 && (
              <>
                <IconButton 
                  onClick={handlePrevPhoto}
                  sx={{
                    position: 'absolute',
                    left: 8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    bgcolor: 'rgba(255,255,255,0.7)',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                    width: 30,
                    height: 30,
                    zIndex: 4,
                    '& .MuiSvgIcon-root': { fontSize: 18 }
                  }}
                >
                  <ArrowBackIosNewIcon />
                </IconButton>
                
                <IconButton
                  onClick={handleNextPhoto}
                  sx={{
                    position: 'absolute',
                    right: 8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    bgcolor: 'rgba(255,255,255,0.7)',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                    width: 30,
                    height: 30,
                    zIndex: 4,
                    '& .MuiSvgIcon-root': { fontSize: 18 }
                  }}
                >
                  <ArrowForwardIosIcon />
                </IconButton>
                
                {/* Photo counter */}
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 8,
                    right: 8,
                    bgcolor: 'rgba(0,0,0,0.5)',
                    color: 'white',
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    fontSize: '0.75rem',
                    zIndex: 4
                  }}
                >
                  {currentPhotoIndex + 1}/{salonPhotos.length}
                </Box>
              </>
            )}
          </Box>
        ) : (
          // No photos - Gradient background only
          <Box
            sx={{
              position: 'relative',
              height: 200,
              background: `linear-gradient(135deg, ${theme.colors?.primary?.[600] || '#1565c0'} 0%, ${theme.colors?.secondary?.[700] || '#495057'} 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 3
            }}
          >
            <Typography 
              variant="h4" 
              component="h2" 
              fontWeight="bold"
              sx={{
                color: '#ffffff',
                textShadow: '0 3px 12px rgba(0,0,0,0.7)',
                fontSize: '2rem',
                letterSpacing: '0.5px',
                textAlign: 'center'
              }}
            >
              {getLocalizedName(salon)}
            </Typography>
          </Box>
        )}
      </Box>
      
     

      {/* Card Content with Salon Details */}
      <CardContent sx={{ flexGrow: 1, pt: 2, pb: 1 }}>
        <Grid container spacing={2}>
          {metaIconsOnly ? (
            (ownerLabel || typeof employeesCount === 'number') ? (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 0.5 }}>
                  {!!ownerLabel && (
                    <Tooltip title={ownerLabel} placement="top">
                      <Box sx={{ display: 'inline-flex' }}>
                        <IconButton
                          size="small"
                          sx={{
                            backgroundColor: theme.colors?.secondary?.[50] || '#f8f9fa',
                            border: `1px solid ${theme.colors?.secondary?.[200] || '#e9ecef'}`,
                          }}
                        >
                          <PersonIcon
                            fontSize="small"
                            sx={{ color: theme.colors?.secondary?.[700] || '#495057' }}
                          />
                        </IconButton>
                      </Box>
                    </Tooltip>
                  )}

                  {typeof employeesCount === 'number' && (
                    <Tooltip
                      title={`${employeesCount} employé${employeesCount > 1 ? 's' : ''}`}
                      placement="top"
                    >
                      <Box sx={{ display: 'inline-flex' }}>
                        <IconButton
                          size="small"
                          sx={{
                            backgroundColor: theme.colors?.primary?.[50] || '#e3f2fd',
                            border: `1px solid ${theme.colors?.primary?.[100] || '#bbdefb'}`,
                          }}
                        >
                          <GroupsIcon
                            fontSize="small"
                            sx={{ color: theme.colors?.primary?.[700] || '#1565c0' }}
                          />
                        </IconButton>
                        <Typography
                          variant="caption"
                          sx={{ ml: 0.6, fontWeight: 900, color: theme.colors?.primary?.[800] || '#0d47a1' }}
                        >
                          {employeesCount}
                        </Typography>
                      </Box>
                    </Tooltip>
                  )}
                </Box>
              </Grid>
            ) : null
          ) : null}

          {/* Address - Full width on first line */}
          <Grid item xs={12}>
            <Box 
              display="flex" 
              alignItems="flex-start"
              sx={{
                p: 1.5,
                backgroundColor: theme.colors?.secondary?.[50] || '#f8f9fa',
                borderRadius: 2,
                border: `1px solid ${theme.colors?.secondary?.[200] || '#e9ecef'}`,
              }}
            >
              <LocationOnIcon 
                fontSize="small" 
                sx={{ 
                  mr: 1.5, 
                  color: theme.colors?.primary?.[500] || '#1976d2', 
                  mt: 0.2,
                  fontSize: '1.2rem'
                }} 
              />
              <Typography 
                variant="body2" 
                sx={{
                  color: theme.colors?.primary?.[700] || '#1565c0',
                  fontWeight: 500,
                  lineHeight: 1.5
                }}
              >
                {salon.formatted_address || t('salon_card.address_not_specified')}
              </Typography>
            </Box>
          </Grid>

          {/* Contact Info - Téléphone et Email sur la même ligne sur mobile */}
          {(salon.phone || salon.email) && (
            <Grid item xs={12}>
              <Box 
                display="flex" 
                gap={1}
                flexWrap="wrap"
                sx={{
                  '& > *': {
                    flex: { xs: '1 1 auto', sm: '1 1 calc(50% - 4px)' }
                  }
                }}
              >
                {/* Phone - only if specified */}
                {salon.phone && (
                  <Box 
                    display="flex" 
                    alignItems="center"
                    sx={{
                      p: 1.5,
                      backgroundColor: theme.colors?.primary?.[50] || '#e3f2fd',
                      borderRadius: 2,
                      border: `1px solid ${theme.colors?.primary?.[100] || '#bbdefb'}`,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: theme.colors?.primary?.[100] || '#bbdefb',
                        transform: 'translateY(-2px)'
                      }
                    }}
                  >
                    <PhoneIcon 
                      fontSize="small" 
                      sx={{ 
                        mr: 1.5, 
                        color: theme.colors?.primary?.[600] || '#1565c0',
                        fontSize: '1.1rem'
                      }} 
                    />
                    <Typography 
                      variant="body2" 
                      noWrap
                      sx={{
                        color: theme.colors?.primary?.[700] || '#1565c0',
                        fontWeight: 500
                      }}
                    >
                      {salon.phone}
                    </Typography>
                  </Box>
                )}

                {/* Email - only if specified */}
                {salon.email && (
                  <Box 
                    display="flex" 
                    alignItems="center"
                    sx={{
                      p: 1.5,
                      backgroundColor: theme.colors?.secondary?.[50] || '#f8f9fa',
                      borderRadius: 2,
                      border: `1px solid ${theme.colors?.secondary?.[200] || '#e9ecef'}`,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: theme.colors?.secondary?.[100] || '#e9ecef',
                        transform: 'translateY(-2px)'
                      }
                    }}
                  >
                    <EmailIcon 
                      fontSize="small" 
                      sx={{ 
                        mr: 1.5, 
                        color: theme.colors?.secondary?.[600] || '#6c757d',
                        fontSize: '1.1rem'
                      }} 
                    />
                    <Typography 
                      variant="body2" 
                      noWrap
                      sx={{
                        color: theme.colors?.secondary?.[700] || '#495057',
                        fontWeight: 500
                      }}
                    >
                      {salon.email}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>
          )}

          {/* Opening Hours - Full width */}
          {showOpeningHours && (
            <Grid item xs={12}>
              <Box 
                display="flex" 
                flexDirection="column" 
                alignItems="center" 
                mb={0.5}
                sx={{
                  p: 2,
                  backgroundColor: theme.colors?.primary?.[50] || '#e3f2fd',
                  borderRadius: theme.borderRadius?.lg || '12px',
                  border: `2px solid ${theme.colors?.primary?.[100] || '#bbdefb'}`,
                  boxShadow: `0 2px 8px ${theme.colors?.primary?.[50] || 'rgba(0,0,0,0.05)'}`,
                }}
              >
                <Box display="flex" alignItems="center" mb={1.5}>
                  <AccessTimeIcon 
                    fontSize="small" 
                    sx={{ 
                      mr: 1.5, 
                      color: theme.colors?.primary?.[600] || '#1565c0',
                      fontSize: '1.3rem'
                    }} 
                  />
                  <Typography 
                    variant="h6" 
                    sx={{
                      color: theme.colors?.primary?.[700] || '#1565c0',
                      fontWeight: 'bold',
                      fontSize: '1.1rem'
                    }}
                  >
                    {t('salon_card.opening_hours')}
                  </Typography>
                </Box>
                <Box sx={{ width: '100%', minHeight: 100, textAlign: 'center' }}>
                  {salon.opening_hours ? (
                    (() => {
                      // Normalize keys to lowercase French day names
                      const normalized = Object.entries(salon.opening_hours).reduce((acc, [k, v]) => {
                        const key = (k || '').toString().toLowerCase();
                        acc[key] = v;
                        return acc;
                      }, {});
                      const today = getTodayFrench();
                      const todayHours = normalized[today];
                      const remainingDays = FRENCH_DAYS_ORDER.filter(d => d !== today && normalized[d]);
                      const leftCol = remainingDays.slice(0, 3);
                      const rightCol = remainingDays.slice(3, 6);

                      return (
                        <>
                          {/* Today highlighted row */}
                          {todayHours && (
                            <Box sx={{
                              px: 1.5,
                              py: 1,
                              mb: 1.5,
                              borderLeft: '4px solid',
                              borderColor: theme.colors?.primary?.[500] || '#1976d2',
                              backgroundColor: theme.colors?.primary?.[100] || '#bbdefb',
                              borderRadius: theme.borderRadius?.md || '8px',
                              boxShadow: `0 2px 6px ${theme.colors?.primary?.[100] || 'rgba(0,0,0,0.1)'}`,
                              border: `1px solid ${theme.colors?.primary?.[200] || '#90caf9'}`
                            }}>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  fontSize: '1rem', 
                                  color: theme.colors?.primary?.[700] || '#1565c0', 
                                  fontWeight: 700,
                                  textAlign: 'center'
                                }}
                              >
                                {t(getDayTranslationKey(today))}: {todayHours.ouverture === 'fermé' ? t('pro_dashboard.common.closed') : `${todayHours.ouverture} - ${todayHours.fermeture}`}
                              </Typography>
                            </Box>
                          )}

                          {/* Remaining days - 2 per line: Lundi+Mardi, Mercredi+Jeudi, Vendredi+Samedi, Dimanche */}
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
                            {(() => {
                              // Group days in pairs: [Lundi, Mardi], [Mercredi, Jeudi], [Vendredi, Samedi], [Dimanche]
                              const pairs = [];
                              for (let i = 0; i < remainingDays.length; i += 2) {
                                pairs.push(remainingDays.slice(i, i + 2));
                              }
                              
                              return pairs.map((pair, idx) => (
                                <Box key={idx} sx={{ display: 'flex', gap: 1, width: '100%' }}>
                                  {pair.map((d) => (
                                    <Typography 
                                      key={d}
                                      variant="body2" 
                                      sx={{ 
                                        fontSize: { xs: '0.7rem', md: '0.75rem' },
                                        color: theme.colors?.secondary?.[600] || '#6c757d',
                                        fontWeight: 500,
                                        textAlign: 'center',
                                        p: 0.5,
                                        backgroundColor: theme.colors?.secondary?.[50] || '#f8f9fa',
                                        borderRadius: '4px',
                                        flex: 1
                                      }}
                                    >
                                      <strong>{t(getDayTranslationKey(d))}:</strong> {normalized[d].ouverture === 'fermé' ? t('pro_dashboard.common.closed') : `${normalized[d].ouverture} - ${normalized[d].fermeture}`}
                                    </Typography>
                                  ))}
                                </Box>
                              ));
                            })()}
                          </Box>
                        </>
                      );
                    })()
                  ) : (
                    <Box
                      sx={{
                        p: 2,
                        backgroundColor: theme.colors?.secondary?.[50] || '#f8f9fa',
                        borderRadius: theme.borderRadius?.md || '8px',
                        border: `1px solid ${theme.colors?.secondary?.[200] || '#e9ecef'}`,
                      }}
                    >
                      <Typography 
                        variant="body2" 
                        sx={{
                          color: theme.colors?.secondary?.[600] || '#6c757d',
                          fontStyle: 'italic',
                          textAlign: 'center'
                        }}
                      >
                        Non spécifié
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Grid>
          )}
        </Grid>
      </CardContent>

    </SalonCard>
  );
};

export default BasicInfo;
